/**
 * Tries to clone an object deeply without removing functions and other non-JSONable properties.
 *
 * @param input {object} The input object which should be cloned
 * @param options
 * @return {object} The cloned object
 */

const clone = <InputType extends object | []>(input: InputType): InputType =>
    JSON.parse(JSON.stringify(input))

export interface TraverseMeta {
    depth: number
    keyPath: string
    ancestors: Array<unknown>
}
const defaultMeta: TraverseMeta = {
    depth: 0,
    keyPath: "",
    ancestors: []
}

export interface TraverseOptions {
    recursive?: boolean
}
const defaultOptionsTraverse: Required<TraverseOptions> = {
    recursive: true
}

export type TraverseCallback = <TInput>(
    key: string | number,
    value: unknown,
    obj: TInput,
    meta: TraverseMeta
) => void

const traverse = <TInput extends object | []>(
    input: TInput,
    callback: TraverseCallback,
    options: TraverseOptions = defaultOptionsTraverse,
    meta: TraverseMeta = defaultMeta
) => {
    meta = { ...defaultMeta, ...meta }
    options = { ...defaultOptionsTraverse, ...options }

    Object.keys(input).forEach(key => {
        const currentMeta: TraverseMeta = {
            ...meta,
            keyPath: meta.keyPath ? `${meta.keyPath}.${key}` : key
        }

        callback(key, (input as Record<string, unknown>)[key], input, currentMeta)

        if (
            options.recursive &&
            !!(input as Record<string, unknown>)[key] &&
            typeof (input as Record<string, unknown>)[key] === "object"
        ) {
            const value = (input as Record<string, unknown>)[key] as object | []
            traverse(value, callback, options, {
                ...currentMeta,
                ancestors: [...currentMeta.ancestors, input],
                depth: currentMeta.depth + 1
            })
        }
    })
}

export interface RemovePropertiesMatchingValueOption {
    recursive?: boolean
    modifyInputObject?: boolean
}

const defaultOptionsRemoveObjectPropertiesMatchingValue: RemovePropertiesMatchingValueOption =
    {
        recursive: true,
        modifyInputObject: true
    }

export const removeObjectPropertiesMatchingValue = <InputType extends object | []>(
    input: InputType,
    valueToRemove: unknown,
    options = defaultOptionsRemoveObjectPropertiesMatchingValue
) => {
    options = { ...defaultOptionsRemoveObjectPropertiesMatchingValue, ...options }

    const output = options.modifyInputObject ? input : <InputType>clone(input)

    traverse(output, (key, value, obj, meta) => {
        if ((options.recursive || meta.depth === 0) && value === valueToRemove) {
            delete (obj as Record<string, unknown>)[key]
        }
    })

    return output
}
